import Vue from 'vue';
import Vuex from 'vuex';
import Axios, {AxiosError, AxiosInstance} from 'axios';
import {Employee} from '@/interfaces/employee.interface';
import {StoreActionButtonAction} from '@/interfaces/clientOnly/storeActionButtonAction.interface';
import {ShareAccessDialogDataInterface} from '@/interfaces/clientOnly/shareAccessDialogData.interface';
import CrudAccess from '@/classes/clientOnly/permissionTreeResources/interfaces/CrudAccess';
import CrudAccessManager from '@/classes/clientOnly/permissionTreeResources/classes/CrudAccessManager';
import {SnackbarAction} from '@/interfaces/clientOnly/snackbarAction';
import {UnauthorizedEvent} from '@/components/app/unauthorizedDialog/unauthorized.helper';
import {LeanDocument, Types} from 'mongoose';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {setNotificationPartner} from '@/components/salesPartner/partnerNotificationPaths';
import {Route} from 'vue-router';
import CookieManager from '@/classes/clientOnly/CookieManager.class';
import {IncomingNotification, NotificationLike} from '@/types/NotificationLike.type';
import {NotificationBus} from '@/busses/NotificationBus';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {VanillaException} from '@/exceptions/VanillaException';
import {UserSurveyDocument} from "@/documentTypes/survey-manager/UserSurvey.document";
import {notificationGroupKey} from '@/helpers/notificationGroups.helper';
import {APP_TITLE} from '@/helpers/seo.helper';
import {
    bindToastStore,
    createMessageToast,
    enqueueToast,
    removeMessageToasts,
    SnackbarInput,
    ToastItem,
} from '@/helpers/undoToast';

import {isViewAsRoleReadOnlyBody, VIEW_AS_ROLE_READ_ONLY_TEXT, ViewAsRole, writeViewAsRoleFlag} from '@/helpers/viewAsRole.helper';

Vue.use(Vuex);

export type ViewAsPartner = { id: string; name: string };

/** „Ansicht als Rolle“: Hinweis auf die Schreibsperre höchstens alle 4 s (mehrere abgelehnte Anfragen auf einmal) */
let lastReadOnlyHint = 0;

/** Zeigt bei einer in der Ansicht abgelehnten schreibenden Anfrage einmal den verständlichen Hinweis */
function addViewAsRoleInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use(undefined, (error: AxiosError) => {
        if (isViewAsRoleReadOnlyBody(error?.response?.data) && Date.now() - lastReadOnlyHint > 4000) {
            lastReadOnlyHint = Date.now();
            store.commit('showSnackbar', {text: VIEW_AS_ROLE_READ_ONLY_TEXT, btnColor: 'warning', timeout: 5000});
        }
        return Promise.reject(error);
    });
    return instance;
}

const VIEW_AS_PARTNER_KEY = 'vanilla.viewAsPartner';

function readViewAsPartner(): ViewAsPartner | null {
    try {
        const value = sessionStorage.getItem(VIEW_AS_PARTNER_KEY);
        return value ? JSON.parse(value) as ViewAsPartner : null;
    } catch (e) {
        return null;
    }
}

const store = new Vuex.Store({
    state: {
        routeCache: undefined as Route | undefined,
        loading: false,
        appReady: false,
        socketConnected: false,
        title: APP_TITLE,
        axios: null as null | AxiosInstance,
        loginUserId: null as Types.ObjectId | null,
        user: null as null | Employee,
        userType: null as null | LoginTypes,
        originUserId: undefined as Types.ObjectId | undefined,
        /** „Ansicht als Rolle“ (nur lesen) läuft: Rolle und Ende (aus checkLogin / Start), sonst null */
        viewAsRole: null as ViewAsRole | null,
        error: undefined as AxiosError | undefined,
        accessToken: null as null | string,
        /** Open "Zugriff verweigert" dialog (403), see unauthorized-dialog.app.vue */
        unauthorized: null as UnauthorizedEvent | null,
        uploadProgress: 0,
        showSurveyDialog: false,
        currentSurvey: undefined as undefined | LeanDocument<UserSurveyDocument>,
        snackbar: {
            show: false,
            text: '',
            btnColor: 'pink',
            timeout: -1,
            actions: [] as SnackbarAction[],
            info: undefined as string | VanillaException | undefined,
        },
        /** Global toast queue (snackbar, undo, interrupt), see helpers/undoToast.ts */
        toasts: [] as ToastItem[],
        actionButton: {
            show: false,
            actions: [] as StoreActionButtonAction[],
        },
        openOrders: null as number | null,
        openSubmissions: null as number | null,
        openTerminations: null as number | null,
        openTeacherDocuments: null as number | null,
        pendingPaychecksCount: null as number | null,
        /** Online make-up claims waiting for an offer (menu "Nachholrunden online") */
        waitingMakeupOffers: null as number | null,
        shareAccessDialogInfo: {
            show: false,
        } as ShareAccessDialogDataInterface,
        crudAccessManager: new CrudAccessManager(),
        guiAccess: [] as NavigationDrawerAccess[],
        cookieManager: new CookieManager(),
        showCookieDialog: false,
        rtcNotifications: [] as NotificationLike[],
        /** Read-only "view as agency" of employees: the agency whose partner area is shown */
        viewAsPartner: readViewAsPartner(),
        /** True while a partner view is open in read-only mode; only then the sales-partner requests carry the agency */
        viewAsPartnerActive: false,
    },
    mutations: {
        setSocketConnected(state, value: boolean) {
            state.socketConnected = value;
        },
        setRouteCache(state, route: Route) {
            state.routeCache = route;
        },
        clearRouteCache(state) {
            state.routeCache = undefined;
        },
        setCrudAccess(state, crudAccess: CrudAccess) {
            state.crudAccessManager = new CrudAccessManager(crudAccess);
        },
        setGuiAccess(state, guiAccess: NavigationDrawerAccess[]) {
            state.guiAccess = guiAccess;
        },
        setViewAsPartner(state, partner: ViewAsPartner | null) {
            state.viewAsPartner = partner;
            try {
                if (partner) {
                    sessionStorage.setItem(VIEW_AS_PARTNER_KEY, JSON.stringify(partner));
                } else {
                    sessionStorage.removeItem(VIEW_AS_PARTNER_KEY);
                }
            } catch (e) {
                // Storage is optional
            }
        },
        setViewAsPartnerActive(state, active: boolean) {
            state.viewAsPartnerActive = active;
        },
        setTitle(state, title: string) {
            state.title = title || APP_TITLE;
            document.title = state.title;
        },
        unsetTitle(state) {
            state.title = APP_TITLE;
            document.title = state.title;
        },
        setUploadProgress(state, progress: number) {
            state.uploadProgress = progress;
        },
        user(state, meta) {
            state.user = meta;
        },
        setUserType(state, type: LoginTypes) {
            state.userType = type;
            // Notifications of partner logins open the call view instead of office pages
            setNotificationPartner(type === LoginTypes.PARTNER);
        },
        setLoginUserId(state, userId: Types.ObjectId) {
            state.loginUserId = userId;
        },
        login(state, accessToken?: string) {
            if (accessToken) {
                state.accessToken = accessToken;
                state.axios = addViewAsRoleInterceptor(Axios.create({
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    baseURL: process.env.VUE_APP_API_PATH,
                    withCredentials: true,
                }));
            } else {
                state.accessToken = null;
                state.axios = addViewAsRoleInterceptor(Axios.create({
                    baseURL: process.env.VUE_APP_API_PATH,
                    withCredentials: true,
                }));
            }
        },
        logout(state) {
            state.axios = null;
            state.user = null;
        },
        setError(state, error?: AxiosError) {
            state.error = error;
        },
        /** Opens (event) or closes (nothing) the "Zugriff verweigert" dialog; a second 403 while it is open is dropped */
        setUnauthorized(state, event?: UnauthorizedEvent) {
            if (event && state.unauthorized) {
                return;
            }
            state.unauthorized = event || null;
        },
        showSurveyDialog(state, obj: {value: boolean; survey?: LeanDocument<UserSurveyDocument> }) {
            state.showSurveyDialog = obj.value;
            state.currentSurvey = obj.survey || undefined;
        },
        /** Legacy: hides all snackbar messages (undo toasts stay) */
        hideSnackbar(state) {
            state.snackbar.show = false;
            removeMessageToasts(state.toasts);
        },
        /** Legacy entry point (also used by api.class.ts / router): shows a message toast */
        showSnackbar(state, obj: SnackbarInput) {
            state.snackbar = {
                show: true,
                text: obj.text,
                btnColor: obj.btnColor || 'pink',
                timeout: obj.timeout || 3000,
                actions: obj.actions || [],
                info: obj.info,
            };
            enqueueToast(state.toasts, createMessageToast(obj));
        },
        pushToast(state, toast: ToastItem) {
            enqueueToast(state.toasts, toast);
        },
        removeToast(state, id: number) {
            const index = state.toasts.findIndex(t => t.id === id);
            if (index >= 0) {
                state.toasts.splice(index, 1);
            }
            if (!state.toasts.some(t => t.kind === 'message')) {
                state.snackbar.show = false;
            }
        },
        patchToast(state, data: { id: number; patch: Partial<ToastItem> }) {
            const toast = state.toasts.find(t => t.id === data.id);
            if (toast) {
                Object.assign(toast, data.patch);
            }
        },
        setActions(state, actions: StoreActionButtonAction[]) {
            state.actionButton.actions = actions;
            state.actionButton.show = true;
        },
        hideActions(state, options?: { clearActions?: boolean }) {
            if (options && options.clearActions) {
                state.actionButton.actions = [];
            }
            state.actionButton.show = false;
        },
        updateOpenOrders(state, value: number) {
            state.openOrders = value;
        },
        updateOpenSubmissions(state, value: number) {
            state.openSubmissions = value;
        },
        updateOpenTerminations(state, value: number) {
            state.openTerminations = value;
        },
        updatePendingPaychecksCount(state, value: number) {
            state.pendingPaychecksCount = value;
        },
        updateWaitingMakeupOffers(state, value: number) {
            state.waitingMakeupOffers = value;
        },
        updateOpenTeacherDocuments(state, value: number) {
            state.openTeacherDocuments = value;
        },
        setShareAccessDialogInfo(state, value: ShareAccessDialogDataInterface) {
            state.shareAccessDialogInfo = value;
        },
        setAppReady(state, value: boolean) {
            state.appReady = value;
        },
        setLoadingState(state, value: boolean) {
            state.loading = value;
        },
        setOriginUserId(state, value: Types.ObjectId | undefined) {
            state.originUserId = value;
        },
        setViewAsRole(state, value: ViewAsRole | null | undefined) {
            state.viewAsRole = value || null;
            writeViewAsRoleFlag(!!value);
        },
        showCookieDialog(state, value: boolean) {
            state.showCookieDialog = value;
        },
        /**
         * New notification from the socket. `showInApp: false` in the value suppresses the pop-up (settings).
         * A visible pop-up of the same group (same to-do, same log entry …) is replaced and counted.
         */
        addRtcNotification(state, value: IncomingNotification & { showInApp?: boolean }) {
            const notification: NotificationLike = {...value, showInApp: value.showInApp !== false, groupCount: 1};
            const key = notificationGroupKey(notification);
            if (notification.showInApp && key) {
                state.rtcNotifications
                    .filter((n) => n.showInApp && notificationGroupKey(n) === key)
                    .forEach((n) => {
                        n.showInApp = false;
                        notification.groupCount = (notification.groupCount || 1) + (n.groupCount || 1);
                    });
            }
            state.rtcNotifications.push(notification);

            NotificationBus.$emit('pushRtc', notification);

            if (notification.timeout) {
                setTimeout(() => {
                    notification.showInApp = false;
                }, notification.timeout);
            }
        },
        async hideNotification(state, id: Types.ObjectId) {
            state.rtcNotifications
                .filter(n => String(n._id) === String(id))
                .forEach((n) => {
                    n.showInApp = false;
                });
        },
        /** Hides pop-ups without announcing it to the other tabs (hidden there already, or synced by the server) */
        hidePopups(state, ids: unknown[]) {
            const keys = new Set((ids || []).map(String));
            state.rtcNotifications
                .filter((n) => n.showInApp && keys.has(String(n._id)))
                .forEach((n) => {
                    n.showInApp = false;
                });
        },
    },
    actions: {},
    modules: {},
});

bindToastStore(store);

export default store;
